// HandleHoverCard - wraps any inline node; on desktop hover/focus (or first tap
// on touch) shows a glass profile card: banner, avatar, badges, duel record,
// Follow / Message / Challenge. Silent when the profile can't be loaded.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Box from '@mui/material/Box';
import { Link, useNavigate } from 'react-router-dom';
import { loadProfile, socialFetch, patchCachedProfile } from './socialApi';
import { Avatar, BadgeIcons, FONT, glassSx, gradientFor, pill, popIn, useSocialT } from './socialUi';

const CARD_W = 320;
// Message opens the DM room in the charting page's social dock (deep link handled by the aurora shell).
export const DM_ROUTE = (handle) => `/app/charting?dm=${encodeURIComponent(handle)}`;

export const challengePath = (handle) => `/practice?tab=compete&mode=duel&opponent=${encodeURIComponent(handle)}`;

const isCoarse = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(hover: none)').matches;

export function ProfileActions({ T, p, handle, onNavigate }) {
  const navigate = useNavigate();
  const [following, setFollowing] = useState(Boolean(p.you_follow));
  const [busy, setBusy] = useState(false);
  const go = (path) => { if (onNavigate) onNavigate(); navigate(path); };

  const toggleFollow = async () => {
    if (busy) return;
    const was = following;
    setFollowing(!was); setBusy(true);
    const r = await socialFetch(`/api/duels/players/${encodeURIComponent(p.handle || handle)}/follow`, { method: was ? 'DELETE' : 'POST' });
    if (!r.ok) setFollowing(was); else patchCachedProfile(p.handle || handle, { you_follow: !was });
    setBusy(false);
  };

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Box component="button" type="button" aria-pressed={following} disabled={busy} onClick={toggleFollow} sx={pill(T, { primary: !following, busy })}>
        {following ? 'Following' : 'Follow'}
      </Box>
      {DM_ROUTE && p.can_dm !== false && (
        <Box component="button" type="button" onClick={() => go(DM_ROUTE(p.handle))} sx={pill(T)}>Message</Box>
      )}
      {p.can_duel !== false && (
        <Box component="button" type="button" onClick={() => go(challengePath(p.handle))} sx={pill(T)}>Challenge</Box>
      )}
    </Box>
  );
}

export function ProfileCardBody({ T, p, handle, onNavigate }) {
  const d = (p.stats && p.stats.duels) || {};
  const stat = (label, v) => (
    <Box sx={{ flex: 1, textAlign: 'center' }}>
      <Box sx={{ fontSize: 15, fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>{v}</Box>
      <Box sx={{ fontSize: 11, color: T.muted, mt: '1px' }}>{label}</Box>
    </Box>
  );

  return (
    <>
      <Box sx={{ height: 64, background: p.banner_url ? `center/cover url("${String(p.banner_url).replace(/"/g, '%22')}")` : gradientFor(p.handle), opacity: 0.95 }} />
      <Box sx={{ px: 2, pb: 2, mt: '-24px' }}>
        <Avatar user={p} size={56} ring={`0 0 0 3px ${T.dark ? '#262629' : '#fff'}`} />
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Box component={Link} to={`/@${p.handle}`} onClick={onNavigate} sx={{ color: 'inherit', textDecoration: 'none', fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em', '&:hover': { textDecoration: 'underline' } }}>
            {p.display_name || p.handle}
          </Box>
          <BadgeIcons badges={p.badges} size={15} />
        </Box>
        <Box sx={{ fontSize: 13, color: T.inkSoft, display: 'flex', gap: 0.75, alignItems: 'center' }}>
          @{p.handle}
          {p.follows_you && <Box component="span" sx={{ fontSize: 10.5, px: 0.8, py: '1px', borderRadius: 999, bgcolor: T.hover, color: T.inkSoft }}>Follows you</Box>}
        </Box>
        {p.bio && <Box sx={{ mt: 1, fontSize: 13.5, lineHeight: 1.4, color: T.ink, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.bio}</Box>}
        <Box sx={{ mt: 1.5, py: 1, display: 'flex', borderRadius: '12px', bgcolor: T.hover }}>
          {stat('Duels W-L-D', `${d.w || 0}-${d.l || 0}-${d.d || 0}`)}
          {stat('Arena rank', p.stats && p.stats.arena_rank ? `#${p.stats.arena_rank}` : '-')}
          {stat('Followers', (p.stats && p.stats.followers) || 0)}
        </Box>
        <Box sx={{ mt: 1.5 }}><ProfileActions T={T} p={p} handle={handle} onNavigate={onNavigate} /></Box>
      </Box>
    </>
  );
}

function CardSkeleton({ T }) {
  const bar = (w, h = 12) => <Box sx={{ width: w, height: h, borderRadius: 6, bgcolor: T.hover, mt: 1 }} />;
  return (
    <Box aria-busy="true" sx={{ p: 2 }}>
      <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: T.hover }} />
      {bar(140, 16)}{bar(90)}{bar('100%', 44)}
    </Box>
  );
}

export default function HandleHoverCard({ handle, children, delay = 350, disabled }) {
  const T = useSocialT();
  const anchor = useRef(null);
  const timer = useRef(null);
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ loading: false, result: null });
  const [pos, setPos] = useState(null);
  const id = `hc-${String(handle)}`;

  const clear = () => clearTimeout(timer.current);
  const show = useCallback((wait) => {
    if (disabled) return;
    clear();
    timer.current = setTimeout(() => {
      setOpen(true);
      setState((s) => (s.result ? s : { loading: true, result: null }));
      loadProfile(handle).then((r) => setState({ loading: false, result: r }));
    }, wait);
  }, [handle, disabled]);
  const hide = useCallback((wait = 180) => { clear(); timer.current = setTimeout(() => setOpen(false), wait); }, []);
  useEffect(() => clear, []);

  const r = state.result;
  const p = r && r.ok && r.data && !r.data.redirect ? r.data : null;
  const priv = r && r.status === 403 && r.data ? r.data : null;
  // Failed (404/501/network): never show a card at all.
  const failed = r && !p && !priv;
  const visible = open && !failed;

  useLayoutEffect(() => {
    if (!visible || !anchor.current) return;
    const rc = anchor.current.getBoundingClientRect();
    const h = 330;
    const up = window.innerHeight - rc.bottom < h && rc.top > h;
    const left = Math.max(8, Math.min(rc.left, window.innerWidth - CARD_W - 8));
    setPos({ left, ...(up ? { bottom: window.innerHeight - rc.top + 8 } : { top: rc.bottom + 8 }) });
  }, [visible, state.loading, p]);

  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e) => { if (anchor.current && !anchor.current.contains(e.target) && !e.target.closest('[data-hovercard]')) setOpen(false); };
    document.addEventListener('keydown', onKey); document.addEventListener('pointerdown', onDown);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('pointerdown', onDown); };
  }, [visible]);

  const onClickCapture = (e) => {
    // First tap on touch opens the card instead of navigating; the card links on.
    if (isCoarse() && !open) { e.preventDefault(); e.stopPropagation(); clear(); show(0); }
  };

  return (
    <>
      <Box component="span" ref={anchor} sx={{ display: 'inline' }}
        onMouseEnter={() => show(delay)} onMouseLeave={() => hide()}
        onFocus={() => show(0)} onBlur={() => hide(120)}
        onClickCapture={onClickCapture} aria-describedby={visible ? id : undefined}>
        {children}
      </Box>
      {visible && pos && createPortal(
        <Box id={id} role="dialog" aria-label={`@${handle} profile`} data-hovercard
          onMouseEnter={clear} onMouseLeave={() => hide()}
          sx={{ position: 'fixed', zIndex: 1600, width: CARD_W, maxWidth: 'calc(100vw - 16px)', ...pos, borderRadius: '18px', overflow: 'hidden', ...glassSx(T), ...popIn }}>
          {state.loading && !r && <CardSkeleton T={T} />}
          {p && <ProfileCardBody T={T} p={p} handle={handle} onNavigate={() => setOpen(false)} />}
          {priv && (
            <Box sx={{ p: 2 }}>
              <Avatar user={priv} size={52} />
              <Box sx={{ mt: 1, fontWeight: 700, fontSize: 16 }}>{priv.display_name || priv.handle}</Box>
              <Box sx={{ fontSize: 13, color: T.inkSoft }}>@{priv.handle}</Box>
              <Box sx={{ mt: 1.25, fontSize: 12.5, color: T.muted, fontFamily: FONT }}>This profile is private.</Box>
            </Box>
          )}
        </Box>,
        document.body,
      )}
    </>
  );
}
